import { DEFAULT_PAGE_SIZE, QUERY_KEY } from "@/constants";
import useDebounce from "@/hooks/useDebounce";
import { useFlightDiscountColumns } from "@/hooks/useFlightDiscountColumns";
import {
  activeDiscountService,
  deleteDiscountService,
  fetchListDiscountService,
} from "@/services/discount.service";
import {
  ActiveType,
  DiscountApplyForType,
  DiscountType,
  FilterState,
  SortBy,
  Type
} from "@/types";
import { ROUTE } from "@/types/router";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Pagination,
  PaginationItem,
  SelectChangeEvent,
  Theme,
  useMediaQuery,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationModal from "../discountManagement/components/DeleteConfirmationModal";
import DiscountStatusFilters from "../discountManagement/components/DiscountStatusFilters";
import DiscountTable from "../discountManagement/components/DiscountTable";
import DiscountTableSkeleton from "../discountManagement/components/DiscountTableSkeleton";
import SkeletonCard from "../discountManagement/components/SkeletonCard";
import CouponCard from "./components/CouponCard";

const FlightDiscountManagementPage = () => {
  const navigation = useNavigate();
  const [page, setPage] = useState<number>(1);
  const initialFilterState: FilterState = {
    field: null,
    order: null,
    active: null,
    discountType: null,
  };
  const [filterValues, setFilterValues] = useState<{
    active: string;
    type: Type | string;
    searchTerm: string;
  }>({
    active: "",
    type: "",
    searchTerm: "",
  });
  const [filter, setFilter] = useState<FilterState>(initialFilterState);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const debouncedSearchTerm = useDebounce(filterValues.searchTerm, 500);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const [discountId, setDiscountId] = useState<string>("");

  const queryClient = useQueryClient();
  const {
    data: discountList,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      QUERY_KEY.fetchFlightDiscountList,
      {
        page,
        query: debouncedSearchTerm,
        field: filter.field,
        order: filter.order,
        active: filter.active,
        discountType: filter.discountType,
      },
    ],
    queryFn: () =>
      fetchListDiscountService({
        page,
        size: DEFAULT_PAGE_SIZE,
        query: filterValues.searchTerm,
        field: filter.field ? filter.field : undefined,
        order: filter.order ? filter.order : undefined,
        active: filter.active ? filter.active : undefined,
        discountType: filter.discountType ? filter.discountType : undefined,
        discountApplyFor: DiscountApplyForType.FLIGHT,
      }),
    staleTime: 5000,
  });

  const handleDeleteDiscount = useMutation({
    mutationFn: deleteDiscountService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.fetchDiscountList });
      refetch();
      setIsOpenConfirmationModal(false);
      toast.success("Discount successfully deleted!");
    },
    onError: () => {
      setIsOpenConfirmationModal(false);
      toast.error("Delete discount failed!");
    },
  });

  const handleActiveDiscount = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      activeDiscountService(id, active),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.fetchDiscountList });
      refetch();
      if (data.active) {
        toast.success("Active discount successfully!");
      } else {
        toast.success("Inactive discount successfully!");
      }
    },
    onError: () => {
      toast.error("Active discount failed!");
    },
  });

  const handleCreateDiscount = () => navigation(ROUTE.CreateFlightDiscount);

  const handleChangePage = useCallback(
    (_: ChangeEvent<unknown>, pageNext: number) => {
      setPage(pageNext);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    },
    [],
  );

  const handleActiveChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value as ActiveType | "";
    setFilterValues((prev) => ({ ...prev, active: newValue }));

    if (isMobile) {
      // Apply filter immediately on mobile
      setFilter((prev) => ({
        ...prev,
        active:
          newValue === "" || newValue === (ActiveType.ALL as ActiveType | "")
            ? null
            : newValue === ActiveType.ACTIVE
              ? "true"
              : "false",
      }));
      setPage(1);
    }
  };
  const handleTypeChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value as Type | "";
    setFilterValues((prev) => ({ ...prev, type: newValue }));

    if (isMobile) {
      setFilter((prev) => ({
        ...prev,
        discountType:
          newValue === "" || newValue === Type.ALL
            ? null
            : newValue === Type.PERCENTAGE
              ? DiscountType.PERCENTAGE
              : DiscountType.FLAT,
      }));

      setPage(1);
    }
  };

  const handleSearchChange = (value: string) => {
    setFilterValues((prev) => ({ ...prev, searchTerm: value }));

    if (isMobile) {
      const timeoutId = setTimeout(() => {
        setFilter((prev) => ({ ...prev }));
        setPage(1);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  };

  const handleApplyFilter = () => {
    const { active, type } = filterValues;

    setFilter({
      ...initialFilterState,
      active:
        active === "" || active === ActiveType.ALL
          ? null
          : active === ActiveType.ACTIVE
            ? "true"
            : "false",
      discountType:
        type === "" || type === Type.ALL
          ? null
          : type === Type.PERCENTAGE
            ? DiscountType.PERCENTAGE
            : DiscountType.FLAT,
    });

    refetch();
    setPage(1);
  };

  const handleResetFilter = () => {
    setFilterValues({
      active: "",
      type: "",
      searchTerm: "",
    });

    setFilter(initialFilterState);
    refetch();
    setPage(1);
  };

  const handleEdit = (id: string) => {
    navigation(`${ROUTE.EditFlightDiscount}/${id}`);
  };

  const handleRemove = (id: string) => {
    setDiscountId(id);
    setIsOpenConfirmationModal(true);
  };

  const handleConfirmationModal = () => {
    handleDeleteDiscount.mutate(discountId);
  };

  const handleToggleActive = (id: string, active: boolean) => {
    handleActiveDiscount.mutate({ id, active });
  };

  const setSortDiscount = ({ column, direction }: SortBy) => {
    console.log(column, direction);
    setFilter({
      ...filter,
      field: column as FilterState["field"],
      order: direction as FilterState["order"],
    });
  };

  const discountColumns = useFlightDiscountColumns(
    discountList?.data ?? [],
    handleToggleActive,
    handleRemove,
  );

  return (
    <section>
      <div className="flex flex-col max-sm:mt-3 max-sm:w-full max-sm:flex-1 max-sm:flex-row max-sm:items-center max-sm:justify-between">
        <div className="-mt-16 flex items-center gap-4 max-sm:mt-0 md:w-1/2">
          <h2 className="text-3xl font-semibold text-text max-sm:text-[18px]">
            Flight Discount Management
          </h2>
        </div>

        <div className="mt-9 flex items-center gap-[8px] max-sm:mt-0 max-sm:gap-0 ">
          <button
            onClick={handleCreateDiscount}
            className="flex h-[32px] w-[127px] items-center justify-center gap-[2px] rounded-xl bg-text text-white max-sm:gap-0 max-sm:rounded-[6px] lg:h-12 lg:w-[191px] lg:gap-1 lg:p-3 lg:px-4 "
          >
            <img src="/icons/plus.svg" alt="plus" />
            <span className="text-sm font-medium lg:text-base">
              Add discount
            </span>
          </button>
        </div>
      </div>

      <div className="md:hidden">
        <div>
          <DiscountStatusFilters
            active={(filterValues.active as ActiveType) ?? ""}
            onActiveChange={handleActiveChange}
            type={(filterValues.type as Type) ?? ""}
            onTypeChange={handleTypeChange}
            searchTerm={filterValues.searchTerm}
            onSearch={handleSearchChange}
          />
        </div>

        <div className="mt-4">
          {isLoading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : discountList?.data && discountList?.data.length > 0 ? (
            <div className="grid grid-cols-1 gap-3">
              {discountList?.data.map((item, idx) => (
                <div key={idx}>
                  <CouponCard
                    hotelId={item.id}
                    title={item.discountName}
                    discountCode={item.discountCode}
                    validDateStart={item.startDate}
                    validDateEnd={item.endDate}
                    usagePerCode={item.usageLimit ?? null}
                    usagePerUser={item.usageLimitPerUser ?? null}
                    discountType={item.discountType}
                    discountValue={item.discountValue}
                    active={item.active ?? true}
                    onEdit={handleEdit}
                    onRemove={handleRemove}
                    onToggleActive={handleToggleActive}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-[300px] w-full items-center justify-center rounded-3xl bg-white text-text">
              <span className="text-lg font-semibold text-text">
                No discount found
              </span>
            </div>
          )}
        </div>

        {discountList?.data && discountList.data.length > 0 && (
          <div className="flex w-full items-center justify-center">
            <Pagination
              shape="rounded"
              className="my-[12px] mt-[24px] self-center"
              page={page}
              count={discountList?.totalPages ?? 0}
              onChange={handleChangePage}
              siblingCount={0}
              boundaryCount={3}
              renderItem={(item) => (
                <PaginationItem
                  slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                  sx={{
                    backgroundColor: "transparent",
                    color: "#94A3B8",
                    fontSize: 16,
                    "&.Mui-selected": {
                      backgroundColor: "#E0E7FF",
                      color: "#4F46E5",
                      fontWeight: "bold",
                    },
                    "&.Mui-disabled": {
                      opacity: 0.5,
                    },
                  }}
                  {...item}
                />
              )}
            />
          </div>
        )}
      </div>

      <div className="mt-5 flex flex-col space-y-8 rounded-3xl bg-white p-5 text-[#1E293B] max-sm:hidden lg:px-8 lg:py-6">
        <DiscountStatusFilters
          active={filterValues.active as "" | ActiveType}
          onActiveChange={handleActiveChange}
          type={filterValues.type as "" | Type}
          onTypeChange={handleTypeChange}
          searchTerm={filterValues.searchTerm}
          onSearch={handleSearchChange}
          onApply={handleApplyFilter}
          onReset={handleResetFilter}
        />

        {isLoading ? (
          <DiscountTableSkeleton
            labels={[
              "Name",
              "Discount code",
              "Discount Amount",
              "Valid Date",
              "Usage per code",
              "Usage per user",
              "Discount Type",
              "Active",
              "Action",
            ]}
          />
        ) : (
          <DiscountTable
            rows={discountColumns}
            labels={[
              "Name",
              "Discount code",
              "Discount Amount",
              "Valid Date",
              "Usage per code",
              "Usage per user",
              "Discount Type",
              "Active",
              "Action",
            ]}
            page={page}
            count={discountList?.totalPages ?? 0}
            onChange={handleChangePage}
            setSort={setSortDiscount}
          />
        )}
      </div>
      <DeleteConfirmationModal
        open={isOpenConfirmationModal}
        onClose={() => {
          setIsOpenConfirmationModal(false);
        }}
        onConfirm={handleConfirmationModal}
      />
    </section>
  );
};

export default FlightDiscountManagementPage;
